"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Spec = {
  value: number;
  suffix: string;
  label: string;
  description: string;
};
const specs: Spec[] = [{
  value: 99,
  suffix: "%",
  label: "EFFICIENCY",
  description: "Advanced filtration systems designed to remove contaminants with near-perfect precision in harsh environments."
}, {
  value: 24,
  suffix: " / 7",
  label: "MONITORING",
  description: "Continuous automated sensor arrays providing real-time data on water quality and chemical balance."
}, {
  value: 15,
  suffix: " YR",
  label: "DURABILITY",
  description: "Corrosion-resistant materials engineered to withstand aggressive chemical exposure and high-pressure flow."
}, {
  value: 500,
  suffix: " GPM",
  label: "CAPACITY",
  description: "High-throughput processing units capable of handling industrial-scale water treatment demands."
}];
function AnimatedNumber({
  target,
  suffix,
  isActive
}: {
  target: number;
  suffix: string;
  isActive: boolean;
}) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isActive) return;
    const duration = 1800;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, target]);
  const formatted = display.toLocaleString("en-US");
  return <span className="font-default tabular-nums tracking-tight">
      {formatted}
      {suffix}
    </span>;
}
export default function TheTreatmentPlantEnvironmentProblem() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="3320" id="the-treatment-plant-environment-problem" className="bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-8 left-6 md:top-10 md:left-10 z-10 font-default text-xs tracking-widest text-foreground/70">
        04 — SYSTEM METRICS
      </div>

      <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-28 pb-20 md:pt-32 md:pb-24 lg:pt-40 lg:pb-40">
        {}
        <div className="relative w-full h-[60vh] lg:h-auto lg:min-h-[800px] overflow-hidden">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_water_treatment_facility.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_water_treatment_facility.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_water_treatment_facility.webp-wvc-sizes" alt="Industrial water treatment plant infrastructure with stainless steel piping and filtration systems." loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out ${isVisible ? "scale-100" : "scale-105"}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/40 lg:to-transparent pointer-events-none" />
        </div>

        {}
        <div className="flex items-center">
          <div className="w-full px-6 md:px-10 lg:pl-16 lg:pr-16 xl:pr-20 py-12 lg:py-0">
            <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              {}
              <h2 className="font-default font-bold text-foreground tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-6xl lg:text-[64px] xl:text-[72px]">
                Solving complex environmental challenges.
              </h2>

              {}
              <p className="font-default text-foreground/80 mt-8 text-base md:text-lg leading-relaxed max-w-[480px]">
                Our treatment solutions address the most demanding environmental problems in industrial water management. 
                By integrating robust hardware with precision engineering, we ensure your facility meets regulatory 
                standards while optimizing operational longevity and resource efficiency.
              </p>

              {}
              <div className="mt-14 border-t border-border">
                {specs.map((spec, i) => <div key={i} data-index={i} className={`grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-center py-7 md:py-8 border-b border-border transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`} style={{
                transitionDelay: `${300 + i * 120}ms`
              }}>
                    {}
                    <div className="text-primary font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-none min-w-[120px] md:min-w-[180px]">
                      <AnimatedNumber target={spec.value} suffix={spec.suffix} isActive={isVisible} />
                    </div>

                    {}
                    <div>
                      <div className="flex items-center gap-2.5">
                        <span aria-hidden="true" className="inline-block w-1 h-1 bg-primary" style={{
                      width: "4px",
                      height: "4px"
                    }} />
                        <span className="font-default text-xs md:text-sm font-semibold tracking-[0.2em] text-foreground">
                          {spec.label}
                        </span>
                      </div>
                      <p className="font-default text-foreground/70 mt-2 text-sm md:text-base leading-relaxed max-w-[380px]">
                        {spec.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              {}
              <div className={`mt-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: `${300 + specs.length * 120 + 100}ms`
            }}>
                <Button asChild variant="link" className="group h-auto p-0 font-default font-medium text-foreground hover:text-primary hover:no-underline tracking-wide text-sm md:text-base">
                  
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}